<template>
  <el-dialog
    custom-class="inventory-dialog"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :modal-append-to-body="false"
  >
    <template slot="title">
      <span class="inventory-dialog__title">查看库存</span>
    </template>

    <div v-loading="loading" class="inventory-body">
      <div v-if="!loading && packageList.length === 0" class="inventory-empty">
        暂无套餐数据
      </div>
      <div
        v-for="(pkg, idx) in packageList"
        :key="pkg.packageId != null ? pkg.packageId : idx"
        class="inv-pkg-card"
      >
        <div class="inv-info-row">
          <span class="inv-label">套餐名称</span>
          <span class="inv-value">{{ pkg.packageName }}</span>
        </div>
        <div class="inv-info-row">
          <span class="inv-label">限量类型</span>
          <span class="inv-value">{{ pkg.limitType === 1 ? "套餐限量" : "奖品限量" }}</span>
        </div>
        <div class="inv-info-row inv-info-row--section-title">
          <span class="inv-label">关联奖品</span>
        </div>

        <!-- 套餐限量：奖品表格不展示库存列 -->
        <template v-if="pkg.limitType === 1">
          <el-table
            :data="pkg.prizeItems || []"
            border
            size="small"
            class="inv-prize-table"
          >
            <el-table-column prop="prizeId" label="奖品ID" align="center" width="100"></el-table-column>
            <el-table-column prop="prizeName" label="奖品名称" align="center" min-width="110"></el-table-column>
            <el-table-column label="奖品类型" align="center" width="110">
              <template slot-scope="{ row }">{{ getPrizeTypeLabel(row.prizeType) }}</template>
            </el-table-column>
            <el-table-column label="是否活动限定" align="center" min-width="120">
              <template slot-scope="{ row }">
                <span v-if="row.isLimit === 1">{{ row.label || "是" }}</span>
                <span v-else>否</span>
              </template>
            </el-table-column>
          </el-table>
          <div class="inv-info-row inv-info-row--mt">
            <span class="inv-label">初始套餐库存</span>
            <span class="inv-value">{{ pkg.isLimit === 1 ? (pkg.limitValue != null ? pkg.limitValue : "–") : "--" }}</span>
          </div>
          <div class="inv-info-row">
            <span class="inv-label">实际总库存</span>
            <span class="inv-value">{{ pkg.isLimit === 1 ? (pkg.totalInventory != null ? pkg.totalInventory : "–") : "--" }}</span>
          </div>
          <div class="inv-info-row inv-pkg-quota-row">
            <span class="inv-label">剩余库存</span>
            <template v-if="pkg.isLimit !== 1">
              <span class="inv-pkg-quota-value">--</span>
            </template>
            <template v-else-if="isEditingPackage(pkg)">
              <div class="inv-edit-row">
                <span class="inv-edit-quota">{{ pkg.leftQuota != null ? pkg.leftQuota : "–" }}</span>
                <el-select v-model="editingPackage.action" size="mini" class="inv-edit-select" @change="onPackageActionChange">
                  <el-option label="增加" value="add"></el-option>
                  <el-option label="减少" value="sub"></el-option>
                </el-select>
                <el-input
                  :value="editingPackage.amount"
                  size="mini"
                  class="inv-edit-input"
                  placeholder="数量"
                  @input="filterPackageAmount"
                ></el-input>
                <el-button size="mini" type="primary" :loading="submitting" @click="confirmAdjustPackage(pkg)">确定</el-button>
                <el-button size="mini" @click="editingPackage = null">取消</el-button>
              </div>
            </template>
            <template v-else>
              <span class="inv-pkg-quota-value">{{ pkg.leftQuota != null ? pkg.leftQuota : "–" }}</span>
              <el-button
                size="mini"
                type="text"
                class="inv-pkg-quota-edit-btn"
                @click="startEditPackage(pkg)"
              >编辑</el-button>
            </template>
          </div>
        </template>

        <!-- 奖品限量：奖品表格内展示每项库存 -->
        <template v-else>
          <el-table
            :data="pkg.prizeItems || []"
            border
            size="small"
            class="inv-prize-table"
          >
            <el-table-column prop="prizeId" label="奖品ID" align="center" width="80"></el-table-column>
            <el-table-column prop="prizeName" label="奖品名称" align="center" min-width="90"></el-table-column>
            <el-table-column label="奖品类型" align="center" width="90">
              <template slot-scope="{ row }">{{ getPrizeTypeLabel(row.prizeType) }}</template>
            </el-table-column>
            <el-table-column label="初始奖品库存" align="center" width="90">
              <template slot-scope="{ row }">
                <span v-if="row.isLimit != 1">不限量</span>
                <span v-else>{{ row.quantity != null ? row.quantity : "–" }}</span>
              </template>
            </el-table-column>
            <el-table-column label="实际总库存" align="center" width="80">
              <template slot-scope="{ row }">
                <span v-if="row.isLimit != 1">–</span>
                <span v-else>{{ row.totalInventory != null ? row.totalInventory : "–" }}</span>
              </template>
            </el-table-column>
            <el-table-column label="剩余库存" align="center" min-width="280">
              <template slot-scope="{ row }">
                <span v-if="row.isLimit != 1">–</span>
                <template v-else-if="isEditingPrize(pkg, row)">
                  <div class="inv-edit-row">
                    <span class="inv-edit-quota">{{ row.leftQuota != null ? row.leftQuota : "–" }}</span>
                    <el-select v-model="editingPrize.action" size="mini" class="inv-edit-select" @change="onPrizeActionChange">
                      <el-option label="增加" value="add"></el-option>
                      <el-option label="减少" value="sub"></el-option>
                    </el-select>
                    <el-input
                      :value="editingPrize.amount"
                      size="mini"
                      class="inv-edit-input"
                      placeholder="数量"
                      @input="filterAmount"
                    ></el-input>
                    <el-button size="mini" type="primary" :loading="submitting" @click="confirmAdjust(pkg)">确定</el-button>
                    <el-button size="mini" @click="editingPrize = null">取消</el-button>
                  </div>
                </template>
                <template v-else>
                  <span>{{ row.leftQuota != null ? row.leftQuota : "–" }}</span>
                  <el-button
                    size="mini"
                    type="text"
                    class="inv-edit-btn"
                    @click="startEditPrize(pkg, row)"
                  >编辑</el-button>
                </template>
              </template>
            </el-table-column>
            <el-table-column label="是否活动限定" align="center" min-width="90">
              <template slot-scope="{ row }">
                <span v-if="row.isLimit === 1">{{ row.label || "是" }}</span>
                <span v-else>否</span>
              </template>
            </el-table-column>
          </el-table>
        </template>
      </div>
    </div>

    <template #footer>
      <el-button @click="dialogVisible = false">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script>
const LOG_TAG = "[查看库存]";

export default {
  name: "InventoryDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    matchId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      packageList: [],
      editingPrize: null,
      editingPackage: null,
      submitting: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetchDetail();
      } else {
        this.packageList = [];
        this.editingPrize = null;
        this.editingPackage = null;
        this.submitting = false;
      }
    },
  },
  methods: {
    isApiSuccess(errorCode) {
      return errorCode === 10000;
    },
    fetchDetail() {
      if (this.matchId == null) {
        console.warn(LOG_TAG, "matchId 为空，无法查询");
        return;
      }
      this.loading = true;
      this.packageList = [];
      this.$http({
        url: this.$util.domain + "/adminapi/competition/detail",
        method: "POST",
        body: JSON.stringify({ id: Number(this.matchId) }),
      })
        .then((res) => {
          const body = res.body || {};
          if (!this.isApiSuccess(body.error)) {
            console.warn(LOG_TAG, "detail fail", body);
            this.$util.errorMsg(body.message || body.msg || "获取库存详情失败");
            return;
          }
          const data = body.data || {};
          this.packageList = data.packageList || [];
          console.log(LOG_TAG, "库存详情获取成功", this.matchId, "套餐数:", this.packageList.length);
        })
        .catch((err) => {
          console.warn(LOG_TAG, "detail error", err);
          this.$util.errorMsg("网络异常，请稍后重试");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isEditingPrize(pkg, row) {
      return (
        this.editingPrize &&
        this.editingPrize.packageId === pkg.packageId &&
        this.editingPrize.prizeId === row.prizeId
      );
    },
    startEditPrize(pkg, row) {
      this.editingPrize = {
        packageId: pkg.packageId,
        prizeId: row.prizeId,
        id: row.id,
        leftQuota: row.leftQuota != null ? row.leftQuota : 0,
        action: "add",
        amount: "",
      };
    },
    getPrizeAmountMax(action, leftQuota) {
      return action === "add" ? 9999 - leftQuota : leftQuota;
    },
    filterAmount(val) {
      let num = val.replace(/\D/g, "");
      if (num !== "") {
        const leftQuota = this.editingPrize.leftQuota != null ? this.editingPrize.leftQuota : 0;
        const max = this.getPrizeAmountMax(this.editingPrize.action, leftQuota);
        const clamped = Math.min(parseInt(num, 10), Math.max(max, 0));
        num = String(clamped);
      }
      // 截断后值与 model 相同时，Vue 不会重新渲染 DOM，需先置 null 触发变更再赋回
      if (this.editingPrize.amount === num) {
        this.editingPrize.amount = null;
        this.$nextTick(() => {
          this.editingPrize.amount = num;
        });
      } else {
        this.editingPrize.amount = num;
      }
    },
    onPrizeActionChange() {
      if (this.editingPrize && this.editingPrize.amount !== "") {
        this.filterAmount(this.editingPrize.amount);
      }
    },
    isEditingPackage(pkg) {
      return this.editingPackage && this.editingPackage.packageId === pkg.packageId;
    },
    startEditPackage(pkg) {
      this.editingPackage = {
        packageId: pkg.packageId,
        id: pkg.id != null ? pkg.id : pkg.packageId,
        leftQuota: pkg.leftQuota != null ? pkg.leftQuota : 0,
        action: "add",
        amount: "",
      };
    },
    getPackageAmountMax(action, leftQuota) {
      return action === "add" ? 9999 - leftQuota : leftQuota;
    },
    filterPackageAmount(val) {
      let num = val.replace(/\D/g, "");
      if (num !== "") {
        const leftQuota = this.editingPackage.leftQuota != null ? this.editingPackage.leftQuota : 0;
        const max = this.getPackageAmountMax(this.editingPackage.action, leftQuota);
        const clamped = Math.min(parseInt(num, 10), Math.max(max, 0));
        num = String(clamped);
      }
      if (this.editingPackage.amount === num) {
        this.editingPackage.amount = null;
        this.$nextTick(() => {
          this.editingPackage.amount = num;
        });
      } else {
        this.editingPackage.amount = num;
      }
    },
    onPackageActionChange() {
      if (this.editingPackage && this.editingPackage.amount !== "") {
        this.filterPackageAmount(this.editingPackage.amount);
      }
    },
    confirmAdjustPackage(pkg) {
      if (!this.editingPackage) return;
      const amount = parseInt(this.editingPackage.amount, 10);
      if (!amount || amount <= 0) {
        this.$util.errorMsg("请输入有效的数量");
        return;
      }
      const delta = this.editingPackage.action === "add" ? amount : -amount;
      this.submitting = true;
      this.$http({
        url: this.$util.domain + "/adminapi/competition/adjustStock",
        method: "POST",
        body: JSON.stringify({
          competitionId: Number(this.matchId),
          type: "1",
          relationId: this.editingPackage.id,
          delta,
        }),
      })
        .then((res) => {
          const body = res.body || {};
          if (!this.isApiSuccess(body.error)) {
            console.warn(LOG_TAG, "adjustStock package fail", body);
            this.$util.errorMsg(body.message || body.msg || "调整套餐库存失败");
            return;
          }
          console.log(LOG_TAG, "调整套餐库存成功", this.editingPackage.packageId, "delta:", delta);
          this.$util.successMsg("调整成功");
          this.editingPackage = null;
          this.fetchDetail();
        })
        .catch((err) => {
          console.warn(LOG_TAG, "adjustStock package error", err);
          this.$util.errorMsg("网络异常，请稍后重试");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    confirmAdjust(pkg) {
      if (!this.editingPrize) return;
      const amount = parseInt(this.editingPrize.amount, 10);
      if (!amount || amount <= 0) {
        this.$util.errorMsg("请输入有效的数量");
        return;
      }
      const delta = this.editingPrize.action === "add" ? amount : -amount;
      this.submitting = true;
      this.$http({
        url: this.$util.domain + "/adminapi/competition/adjustStock",
        method: "POST",
        body: JSON.stringify({
          competitionId: Number(this.matchId),
          type: "2",
          relationId: this.editingPrize.id,
          delta,
        }),
      })
        .then((res) => {
          const body = res.body || {};
          if (!this.isApiSuccess(body.error)) {
            console.warn(LOG_TAG, "adjustStock fail", body);
            this.$util.errorMsg(body.message || body.msg || "调整库存失败");
            return;
          }
          console.log(LOG_TAG, "调整库存成功", this.editingPrize.prizeId, "delta:", delta);
          this.$util.successMsg("调整成功");
          this.editingPrize = null;
          this.fetchDetail();
        })
        .catch((err) => {
          console.warn(LOG_TAG, "adjustStock error", err);
          this.$util.errorMsg("网络异常，请稍后重试");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    getPrizeTypeLabel(type) {
      const map = {
        0: "站内会员",
        1: "头像挂件",
        2: "主题",
        3: "电子证书",
        4: "电子奖牌",
        5: "实物",
      };
      const n = Number(type);
      return Number.isFinite(n) && map[n] != null ? map[n] : type != null ? String(type) : "–";
    },
  },
};
</script>

<style scoped>
.inventory-body {
  padding: 0 4px;
  max-height: 60vh;
  overflow-y: auto;
}
.inventory-empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;
}
.inv-pkg-card {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px 20px 14px;
  margin-bottom: 16px;
}
.inv-pkg-card:last-child {
  margin-bottom: 0;
}
.inv-info-row {
  display: flex;
  align-items: baseline;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 6px;
}
.inv-info-row--section-title {
  margin-bottom: 8px;
}
.inv-info-row--mt {
  margin-top: 12px;
}
.inv-label {
  width: 90px;
  flex-shrink: 0;
  color: #606266;
}
.inv-value {
  flex: 1;
  color: #303133;
  word-break: break-all;
}
.inv-prize-table {
  width: 100%;
}
.inv-edit-row {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
}
.inv-edit-select {
  width: 72px;
  flex-shrink: 0;
}
.inv-edit-input {
  width: 70px;
  flex-shrink: 0;
}
.inv-edit-btn {
  margin-left: 6px;
  padding: 0;
}
.inv-edit-quota {
  flex-shrink: 0;
  color: #303133;
  font-size: 13px;
  margin-right: 2px;
}
.inv-pkg-quota-row {
  align-items: center;
}
.inv-pkg-quota-value {
  color: #303133;
  word-break: break-all;
}
.inv-pkg-quota-edit-btn {
  margin-left: 30px;
  padding: 0;
  flex-shrink: 0;
}
</style>

<style>
.el-dialog.inventory-dialog {
  width: 920px !important;
  max-width: calc(100vw - 48px) !important;
  box-sizing: border-box;
}
.el-dialog.inventory-dialog .el-dialog__header {
  padding: 24px 28px 14px !important;
}
.el-dialog.inventory-dialog .el-dialog__body {
  padding: 4px 28px 10px !important;
}
.el-dialog.inventory-dialog .el-dialog__footer {
  padding: 14px 28px 20px !important;
  text-align: right !important;
}
.inventory-dialog__title {
  font-size: 16px;
  font-weight: bold;
  color: #000000;
}
</style>



// WEBPACK FOOTER //
// InventoryDialog.vue?51ee4370